import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faMagnifyingGlassChart,
  faBalanceScale,
  faFileContract,
  faHandshake
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import './Services.css';
import SEO from "../components/SEO";

// 🔹 stessi titoli/hash di Home ma con basePosition per layout Services
const methodItems = [
  {
    id: 1,
    hash: "1",
    title: "Consulenza Personalizzata",
    description:
      "Ogni cliente è unico. \n Ascoltiamo attentamente le tue esigenze e analizziamo la tua situazione per proporti coperture realmente su misura. \n Non offriamo soluzioni standard, ma percorsi personalizzati che proteggono ciò che per te conta, con trasparenza e supporto continuo.",
    icon: faUser,
    basePosition: { top: '0%', left: '32.5%' }
  },
  {
    id: 2,
    hash: "2",
    title: "Analisi dei Rischi",
    description:
      "Ogni attività o situazione personale comporta vulnerabilità specifiche. \n Con un’analisi mirata individuiamo scenari di rischio concreti, valutiamo impatti economici e legali e verifichiamo la conformità normativa. \nCosì ottieni una protezione completa, efficace e sempre in linea con la legge.",
    icon: faMagnifyingGlassChart,
    basePosition: { top: '25%', right: '8%' }
  },
  {
    id: 3,
    hash: "3",
    title: "Ricerca e Confronto",
    description:
      "Il mercato assicurativo è vasto e complesso: noi lo semplifichiamo per te.\n Confrontiamo coperture, garanzie, condizioni ed esclusioni per individuare l’opzione migliore. \nTi offriamo una panoramica chiara che ti consente di scegliere con consapevolezza la soluzione più vantaggiosa e affidabile.",
    icon: faBalanceScale,
    basePosition: { top: '25%', left: '8%' }
  },
  {
    id: 4,
    hash: "4",
    title: "Gestione delle Polizze",
    description:
      "Ci occupiamo della gestione delle tue polizze: Sottoscrizione, Modifiche, Controlli e Rinnovi.\n Manteniamo le coperture sempre aggiornate, conformi alle normative e in linea con le tue esigenze, liberandoti da ogni onere burocratico e garantendoti serenità continua.",
    icon: faFileContract,
    basePosition: { top: '50%', right: '8%' }
  },
  {
    id: 5,
    hash: "5",
    title: "Assistenza nei Sinistri",
    description:
      "In caso di sinistro ti affianchiamo in ogni fase: \ndalla denuncia alla raccolta della documentazione fino alla liquidazione del danno. Lavoriamo per tutelare i tuoi diritti e garantire che tu ottenga il giusto risarcimento senza complicazioni.",
    icon: faHandshake,
    basePosition: { top: '50%', left: '8%' }
  }
];

function Services() {
  const [activeItem, setActiveItem] = useState(null);
  const methodSectionRef = useRef(null);
  const location = useLocation();

  // Ref per ogni card
  const cardRefs = useRef({});

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- Layout desktop: margini dinamici (come prima)
  const getMarginTop = (id) => {
    if (activeItem === 1) {
      if (id === 2 || id === 3) return '120px';
      if (id === 4 || id === 5) return '80px';
    } else if (activeItem === 2 || activeItem === 3) {
      if (id === 4 || id === 5) return '130px';
    }
    return '0';
  };

  const getGoalMarginTop = () => {
    if (activeItem === 1) {
      return '220px';
    } else if (activeItem === 2 || activeItem === 3) {
      return '240px';
    } else if (activeItem === 4 || activeItem === 5) {
      return '250px';
    }
    return '0';
  };

  // --- Scroll automatico quando arrivo dalla Home ---
  useEffect(() => {
    if (location.state?.hash) {
      const targetId = methodItems.find(
        (item) => item.hash === location.state.hash || item.id.toString() === location.state.hash
      )?.id;

      if (targetId) {
        setActiveItem(targetId);
        setTimeout(() => {
          cardRefs.current[targetId]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 300);
      }
    }
  }, [location.state]);

  // Altezza dinamica container solo desktop
  useEffect(() => {
    if (!isMobile && methodSectionRef.current) {
      const containerHeight = activeItem ? 800 : 570;
      methodSectionRef.current.style.height = `${containerHeight}px`;
    }
  }, [activeItem, isMobile]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <SEO
        title="Il Nostro Metodo | Andolfatto Insurance Broker"
        description="Scopri il metodo di Andolfatto Insurance Broker: consulenza personalizzata, analisi dei rischi, ricerca e confronto delle polizze, gestione e assistenza completa per privati e imprese."
        url="https://andolfattobroker.it/il-nostro-metodo"
      />

      <div className="services-page">
        <section className="services-intro">
          <h1>Il Nostro Metodo</h1>
          {/* 🔽 H2 nascosti SEO-friendly */}
          <h2 className="visually-hidden">
            Consulenza assicurativa personalizzata e analisi dei rischi per una protezione su misura.
          </h2>

          <h2 className="visually-hidden">
            Ricerca, confronto e selezione delle migliori polizze assicurative sul mercato.
          </h2>

          <h2 className="visually-hidden">
            Gestione delle polizze, rinnovi e modifiche con supporto continuo e trasparente.
          </h2>

          <h2 className="visually-hidden">
            Assistenza nei sinistri per garantire risarcimenti rapidi e tutela dei tuoi diritti.
          </h2>

          <h2 className="visually-hidden">
            Il metodo Andolfatto: indipendenza, precisione e protezione al servizio del cliente.
          </h2>
          <p>
            Come broker indipendenti analizziamo le esigenze di ogni cliente e selezioniamo le coperture più adatte tra le migliori soluzioni sul mercato.
          </p>
        </section>

        <section className="method-section" ref={methodSectionRef}>
          <div className="method-container">
            {methodItems.map((item) => (
                <motion.div
                  key={item.id}
                  ref={(el) => (cardRefs.current[item.id] = el)}
                  className={`method-card ${activeItem === item.id ? 'active' : ''}`}
                  style={
                    isMobile
                      ? {}
                      : {
                          ...item.basePosition,
                          marginTop: getMarginTop(item.id),
                          transition: 'margin-top 0.6s ease',
                        }
                  }
                  onClick={() => setActiveItem(activeItem === item.id ? null : item.id)}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                >
                <div className="method-card-header">
                  <FontAwesomeIcon icon={item.icon} className="method-icon" />
                  <h3>{item.title}</h3>
                </div>
                <AnimatePresence>
                  {activeItem === item.id && (
                    <motion.div
                      className="method-card-description"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                    >
                      <p>{item.description}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}

            <motion.div
              className="method-goal"
              style={
                isMobile
                  ? { marginTop: '2rem' }
                  : {
                      marginTop: getGoalMarginTop(),
                      transition: 'margin-top 0.6s ease'
                    }
              }
            >
              <p>Il Nostro Obiettivo</p>
              <p>
                Garantire Protezione, Serenità e Valore, offrendo un Servizio Rapido, Preciso e sempre Focalizzato sulla Protezione e Tutela degli Interessi di chi si affida a noi.
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </motion.div>
  );
}

export default Services;
